<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <!-- Page Content -->
    <div class="content container-fluid">
      <!-- Page Header -->
      <breadcrumb-header :title="title" :path="path" :text="text" />
      <!-- /Page Header -->

      <div class="row">
        <div class="col-md-12">
          <div class="showentries mb-3 custom-select">
            <div class="d-flex align-items-center gap-2"><span>Show</span>
              <select v-model="perPage" @change="current = 1; pendingList()" style="outline: none">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <!-- Bulk Actions Toolbar -->
          <div v-if="selectedRowKeys.length > 0" class="bulk-actions-bar d-flex flex-wrap align-items-center justify-content-between p-3 mb-3 rounded bg-light border shadow-sm">
            <div class="d-flex align-items-center mb-2 mb-md-0">
              <span class="badge bg-primary fs-6 me-2">{{ selectedRowKeys.length }}</span>
              <span class="fw-semibold text-dark">{{ selectedRowKeys.length === 1 ? '1 entry selected' : selectedRowKeys.length + ' entries selected' }}</span>
              <button type="button" class="btn btn-link btn-sm text-decoration-none ms-2 text-muted" @click="clearSelection" :disabled="bulkLoading">
                <i class="fa-solid fa-xmark me-1"></i>Deselect All
              </button>
            </div>
            <div class="d-flex align-items-center flex-wrap gap-2">
              <button
                type="button"
                class="btn btn-success btn-sm d-flex align-items-center gap-1 text-white"
                :disabled="bulkLoading"
                @click="bulkUpdateStatus('Accepted')"
              >
                <span v-if="bulkLoading && activeBulkAction === 'Accepted'" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="fa-solid fa-check"></i>
                Accept Selected
              </button>
              <button
                type="button"
                class="btn btn-danger btn-sm d-flex align-items-center gap-1 text-white"
                :disabled="bulkLoading"
                @click="bulkUpdateStatus('Rejected')"
              >
                <span v-if="bulkLoading && activeBulkAction === 'Rejected'" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="fa-solid fa-xmark"></i>
                Reject Selected
              </button>
              <button
                type="button"
                class="btn btn-warning btn-sm d-flex align-items-center gap-1 text-dark"
                :disabled="bulkLoading"
                @click="bulkRecalculate"
              >
                <span v-if="bulkLoading && activeBulkAction === 'Recalculate'" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="fa-solid fa-arrows-rotate"></i>
                Recalculate Selected
              </button>
            </div>
          </div>

          <div class="table-responsive overflow-visible" v-if="!loading">
            <a-table class="stripped table-hover" :columns="columns" :data-source="userLeavesData" :pagination="false" row-key="id" :scroll="{ x: 1150 }">
              <template #headerCell="{ column }">
                <template v-if="column.key === 'selection'">
                  <div class="form-check d-flex justify-content-center m-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="isAllSelected"
                      :indeterminate.prop="isIndeterminate"
                      @change="toggleSelectAll"
                      title="Select / Deselect All"
                      style="cursor: pointer;"
                    />
                  </div>
                </template>
              </template>
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'selection'">
                  <div class="form-check d-flex justify-content-center m-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="selectedRowKeys.includes(record.id)"
                      @change="toggleSelectRow(record.id)"
                      style="cursor: pointer;"
                    />
                  </div>
                </template>
                <template v-else-if="column.key === 'user_id'">
                  <action-dropdown @banked-time="showBankedTime" :employee="{ id: record.user_id, name: getEmployeeName(record.user_id) }" variant="text" :reportAsEvent="true"
                    @edit="editClicked" @leaves="leavesClicked" @attendance="attendanceClicked" @sick-leave="sickLeaveClicked" @add-face-template="addFaceTemplateClicked" @face-templates="faceTemplatesClicked" @toggle-monitoring="monitorClicked" @report="viewReport" @assign-employee="assignEmployeeClicked" />
                </template>
                <template v-else-if="column.key === 'work_in_minutes'">

                  {{ toTimeString(record.work_in_minutes) }}

                </template>
                <template v-else-if="column.key === 'status'">
                  <div class="text-start">
                    <div class="action-label">
                      <a class="btn btn-white btn-sm" href="javascript:void(0);">
                        <i :class="record.class"></i>
                        {{ record.status }}
                      </a>
                    </div>

                  </div>
                </template>
                <template v-else-if="column.key === 'updated_by'">
                  {{ record.updated_by_user ? (record.updated_by_user.first_name + ' ' + record.updated_by_user.last_name) : (record.updated_by && record.updated_by.first_name ? (record.updated_by.first_name + ' ' + record.updated_by.last_name) : (record.updated_by ? record.updated_by : '--')) }}
                </template>
                <template v-else-if="column.key === 'updated_at'">
                  {{ record.updated_at ? formatDateTimeStandardWithTime(record.updated_at) : '--' }}
                </template>
                <template v-else-if="column.key === 'action'">
                  <div class="text-end">
                    <div class="dropdown dropdown-action">
                      <a href="javascript:void(0);" class="action-icon dropdown-toggle" data-bs-toggle="dropdown"
                        aria-expanded="false"><i class="material-icons">more_vert</i></a>
                      <div class="dropdown-menu dropdown-menu-right">
                        <a class="dropdown-item" href="javascript:;" @click="updateStatus(record.id, 'Accepted')"><i
                            class="fa-solid fa-check m-r-5"></i>Accept</a>
                        <a class="dropdown-item" href="javascript:;" @click="updateStatus(record.id, 'Rejected')"><i
                            class="fa-solid fa-xmark m-r-5"></i>Reject</a>
                        <a class="dropdown-item" href="javascript:;" @click="refreshBankedTime(record)"><i
                            class="fa-solid fa-arrows-rotate m-r-5"></i>Refresh</a>
                        <a class="dropdown-item" href="javascript:;" data-bs-toggle="modal"
                          data-bs-target="#banked_time_details_modal"
                          @click="view(record, getEmployeeName(record.user_id))"><i
                            class="fa-solid fa-eye m-r-5"></i>View</a>
                        <a class="dropdown-item" href="javascript:;" data-bs-toggle="modal"
                          data-bs-target="#edit_banked_time_modal" @click="edit(record)"><i
                            class="fa-solid fa-pencil m-r-5"></i>Edit</a>
                        <!-- <a
                            class="dropdown-item"
                            href="javascript:;"
                            data-bs-toggle="modal"
                            data-bs-target="#delete_approve"
                            @click="deleteClicked(record.id)"
                            ><i class="fa-regular fa-trash-can m-r-5"></i> Delete</a
                          > -->
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </a-table>
          </div>
          <div class="d-flex justify-content-center text-center mt-5" v-else>
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div class="d-flex justify-content-center">
            <a-pagination v-model:current="current" v-model:pageSize="perPage" :total="total" @change="pendingList"
              show-less-items class="mt-3 " :showSizeChanger="false">
              <template #itemRender="{ type, originalElement }">
                <a v-if="type === 'prev'">Previous</a>
                <a v-else-if="type === 'next'">Next</a>
                <component :is="originalElement" v-else></component>
              </template>
            </a-pagination>
          </div>
        </div>
      </div>
    </div>
    <!-- /Page Content -->

    <leaves-admin-modal ref="leaveAdmin" @allLeaves="pendingList"></leaves-admin-modal>
    <sick-leave-details-modal ref="sickLeaveDetails"></sick-leave-details-modal>
    <sick-leave-modal ref="sickLeave"></sick-leave-modal>
    <edit-employee-modal ref="editEmployee" @editedUser="getEmployees"></edit-employee-modal>
    <add-face-template-modal ref="addface"></add-face-template-modal>
    <face-templates-modal ref="faceTemplates"></face-templates-modal>
    <employee-report-modal ref="employeeReportModal"></employee-report-modal>
    <assign-employee-modal ref="assignEmployeeModal" @updated="getEmployees"></assign-employee-modal>
  </div>
  <edit-banked-time-modal ref="editBankedTime"></edit-banked-time-modal>
  <banked-time-details-modal ref="viewBankedTime"></banked-time-details-modal>
  <!-- /Page Wrapper -->
</template>

<script>
import axios from "axios";
import { VUE_APP_API_BASE_URL } from "../../../config";
import { minToTimeString, formatUSDate, formatDateTimeStandardShort, formatDateTimeStandardWithTime } from "@/utils/util-funcitons";
import { dateRangeStart, dateRangeEnd } from "@/utils/util-var";
import ActionDropdown from "@/components/action-dropdown.vue";
import FaceTemplatesModal from "@/components/modal/face-templates-modal.vue";
import AssignEmployeeModal from "@/components/modal/assign-employee-modal.vue";
import { Modal } from "bootstrap";



const columns = [
  {
    title: "",
    key: "selection",
    width: 45,
    align: "center",
  },
  {
    title: "Employee",
    dataIndex: "user_id",
    key: "user_id",
    width: 200,
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    width: 130,
  },
  {
    title: "Value",
    dataIndex: "work_in_minutes",
    key: "work_in_minutes",
    width: 110,
  },
  {
    title: "Comment",
    dataIndex: "comment",
    key: "comment",
    width: 180,
    ellipsis: true,
  },

  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 130,
  },
  {
    title: "Updated By",
    dataIndex: "updated_by",
    key: "updated_by",
    width: 170,
    ellipsis: true,
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    key: "updated_at",
    width: 160,
  },
  {
    title: "Action",
    key: "action",
    text: "end",
    width: 70,
  },
];

// const d = new Date();
// const dateRangeStart = new Date(d.setMonth(d.getMonth() - 1));
// const dateRangeEnd = new Date();
import { monitorMixin } from "@/mixins/monitorMixin";
export default {
  mixins: [monitorMixin],
  components: {
    ActionDropdown,
    FaceTemplatesModal,
    AssignEmployeeModal
  },
  data() {
    return {
      columns,
      title: "Banked Time",
      path: "Dashboard",
      text: "Banked Time",
      userLeavesData: null,
      perPage: 10,
      current: 1,
      total: 0,
      employeeName: "",
      leaveType: null,
      leaveStatus: null,
      employeeData: [],
      loading: false,
      startDate: formatUSDate(dateRangeStart),
      endDate: formatUSDate(dateRangeEnd),
      selectedRowKeys: [],
      bulkLoading: false,
      activeBulkAction: ""
    };
  },
  computed: {
    isAllSelected() {
      const items = this.userLeavesData || [];
      return Boolean(items.length > 0 && this.selectedRowKeys.length === items.length);
    },
    isIndeterminate() {
      return Boolean(this.selectedRowKeys.length > 0 && !this.isAllSelected);
    }
  },
  methods: {
    formatDateTimeStandardWithTime,
    async pendingList() {
      this.selectedRowKeys = [];
      await axios.post(`${VUE_APP_API_BASE_URL}/banked-time/pending-list?page=${this.current}`, { per_page: this.perPage }).then((response) => {
        if (response.data.status) {
          this.userLeavesData = response.data.data.data;
          this.userLeavesData.forEach(e => {
            e['us_date'] = new Date(e['date'])
            let dateVar = new Date(e['date'])
            e['date'] = formatDateTimeStandardShort(dateVar)
          })
          this.current = response.data.data.current_page;
          this.total = response.data.data.total;
        }
      }).catch(function (error) {
        console.log(error);
      })
        .finally(() => {
          this.loading = false;
        });
    },

    edit(item) {
      this.$refs.editBankedTime.updateValues(item);
    },

    view(item, name) {
      this.$refs.viewBankedTime.updateValues(item, name);
    },

    toTimeString(minutes) {
      return minToTimeString(minutes);
    },

    async refreshBankedTime(record) {
      this.$toast.info('Refreshing banked time...');
      const dateVar = record.us_date || record.date;
      await axios.post(`${VUE_APP_API_BASE_URL}/banked-time/get-details`, {
        date: dateVar,
        user_id: record.user_id
      }).then((response) => {
        if (response.data.status) {
          this.$toast.success('Banked time refreshed successfully');
          this.pendingList();
        } else {
          this.$toast.error(response.data.message || 'Failed to refresh banked time');
        }
      }).catch((error) => {
        console.error(error);
        this.$toast.error('An error occurred while refreshing banked time');
      });
    },

    async updateStatus(id, option) {
      await axios.post(`${VUE_APP_API_BASE_URL}/task/${id}/update-status`, {
        status: option,
      }).then((response) => {
        if (response.data.status) {
          this.$toast.success(response.data.message);
          this.pendingList();
        } else {
          this.$toast.error(response.data.message || response.data.errors);
          // this.errorMessage = response.data.message ?? "";
        }

      }).catch(function (error) {
        console.log('error ', error);
      });
    },

    getEmployees() {


      axios.get(`${VUE_APP_API_BASE_URL}/user/user-list`)
        .then((response) => {
          if (response.data.status) {
            this.employeeData = response.data.data;

            this.employeeData.forEach(element => {
              element["text"] = element["first_name"] + " " + element["last_name"];
            });
          } else {
            this.errorMessage = response.data.message ?? "";
          }
        })
        .finally(() => {

        });
    },

    getEmployeeName(id) {
      const name = this.employeeData.find(item => item.id === id);
      return name ? name.text : '-';
    },
    leavesClicked(record) {
      const routeData = this.$router.resolve({ path: '/employee/leaves-employee', query: { id: record.user_id } });
      window.open(routeData.href, '_blank');
    },
    attendanceClicked(record) {
      const routeData = this.$router.resolve({ path: '/employee/attendance-employee', query: { id: record.user_id } });
      window.open(routeData.href, '_blank');
    },
    sickLeaveClicked(record) {
      this.$refs.sickLeave.updateValues(record);
    },
    addFaceTemplateClicked(record) {
      this.$refs.addface.updateValues(record);
    },
    faceTemplatesClicked(employee) {
      this.$refs.faceTemplates.showTemplates(employee);
    },
    editClicked(record) {
      this.$refs.editEmployee.updateValues(record);
    },
    viewReport(record) {
      this.$refs.employeeReportModal.updateValues(record.id, this.startDate, this.endDate);
      const modal = new Modal(document.getElementById('employee_report_modal'));
      modal.show();
    },
    assignEmployeeClicked(record) {
      this.$refs.assignEmployeeModal.updateValues(record);
    },
    toggleSelectAll(e) {
      const items = this.userLeavesData || [];
      if (e.target.checked) {
        this.selectedRowKeys = items.map(item => item.id);
      } else {
        this.selectedRowKeys = [];
      }
    },
    toggleSelectRow(id) {
      const index = this.selectedRowKeys.indexOf(id);
      if (index > -1) {
        this.selectedRowKeys.splice(index, 1);
      } else {
        this.selectedRowKeys.push(id);
      }
    },
    clearSelection() {
      this.selectedRowKeys = [];
    },
    async bulkUpdateStatus(option) {
      if (!this.selectedRowKeys.length) return;
      this.bulkLoading = true;
      this.activeBulkAction = option;
      try {
        const promises = this.selectedRowKeys.map(id =>
          axios.post(`${VUE_APP_API_BASE_URL}/task/${id}/update-status`, {
            status: option
          })
        );
        const results = await Promise.allSettled(promises);
        const successful = results.filter(r => r.status === 'fulfilled' && r.value?.data?.status).length;
        const failed = results.length - successful;

        if (successful > 0) {
          this.$toast.success(`Successfully updated ${successful} record(s) to ${option}`);
        }
        if (failed > 0) {
          this.$toast.error(`Failed to update ${failed} record(s)`);
        }
        this.selectedRowKeys = [];
        this.pendingList();
      } catch (error) {
        console.error('Bulk update error:', error);
        this.$toast.error('An error occurred during bulk update');
      } finally {
        this.bulkLoading = false;
        this.activeBulkAction = '';
      }
    },
    async bulkRecalculate() {
      if (!this.selectedRowKeys.length) return;
      this.bulkLoading = true;
      this.activeBulkAction = 'Recalculate';
      try {
        const promises = this.selectedRowKeys.map(id =>
          axios.get(`${VUE_APP_API_BASE_URL}/task/${id}/recalculate`)
        );
        const results = await Promise.allSettled(promises);
        const successful = results.filter(r => r.status === 'fulfilled' && r.value?.data?.status).length;
        const failed = results.length - successful;

        if (successful > 0) {
          this.$toast.success(`Successfully recalculated ${successful} record(s)`);
        }
        if (failed > 0) {
          this.$toast.error(`Failed to recalculate ${failed} record(s)`);
        }
        this.selectedRowKeys = [];
        this.pendingList();
      } catch (error) {
        console.error('Bulk recalculate error:', error);
        this.$toast.error('An error occurred during bulk recalculation');
      } finally {
        this.bulkLoading = false;
        this.activeBulkAction = '';
      }
    },
  },
  mounted() {
    this.pendingList();
    this.getEmployees();
  }
};
</script>
<style scoped>
.custom-select select {
  appearance: none;
  width: 50px;
  font-size: 14px;
  padding: 2px 5px;
  background-color: #fff;
  border: 1px solid #caced1;
  border-radius: 0.25rem;
  color: #000;
  cursor: pointer;
}

.bulk-actions-bar {
  border-left: 4px solid #099bf8 !important;
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>